/**
 * Static site facts for Calvary Building Services.
 *
 * Single source of truth for the company's name/address/phone (NAP), services,
 * testimonials, service area, and primary nav. The CMS only manages *photos*
 * and the accent color (see lib/siteConfig.ts) — all copy lives here and in the
 * page components.
 */

import { Bath, ChefHat, House, PencilRuler, TreePine } from "lucide-react";
import type { ComponentType, SVGProps } from "react";
import type { ImageSlot } from "./siteConfig";

/** A line icon — lucide icons satisfy this. */
export type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

export const SITE = {
  name: "Calvary Building Services",
  legalName: "Calvary Building Services Inc",
  tagline: "General contracting you can count on.",
  headline: ["Your vision.", "Our expertise.", "Built right."],
  phone: "843-212-2711",
  phoneHref: "tel:+18432122711",
  email: "calvarybuilders1@gmail.com",
  emailHref: "mailto:calvarybuilders1@gmail.com",
  // Service-area business — only city/state/zip are published (footer/contact +
  // JSON-LD). SITE ships in the client bundle, so nothing sensitive lives here.
  address: {
    city: "Mount Pleasant",
    state: "SC",
    zip: "29466",
  },
  hours: "Mon–Fri 8:00AM – 5:00PM",
  // Calvary is a licensed, bonded & insured general contractor. No public
  // license number is published in the approved design; add one here if desired.
  license: "Licensed, Bonded & Insured",
  serviceArea: "Serving Charleston, Mount Pleasant and the surrounding Lowcountry.",
  founded: 2011,
} as const;

export interface Service {
  slug: string;
  /** Two-digit index label ("01"…"05") used in the design's numbered list. */
  num: string;
  title: string;
  /** Short one-liner for cards / nav. */
  blurb: string;
  /** Full description from the design. */
  desc: string;
  icon: IconComponent;
  imageSlot: ImageSlot;
}

export const SERVICES: Service[] = [
  {
    slug: "kitchen-remodeling",
    num: "01",
    title: "Kitchen Remodeling",
    blurb: "Chef-grade kitchens built around how your family lives.",
    desc: "Custom cabinetry, natural-stone counters, and chef-grade layouts designed around how your family actually lives.",
    icon: ChefHat,
    imageSlot: "serviceKitchen",
  },
  {
    slug: "bathroom-renovations",
    num: "02",
    title: "Bathroom Renovations",
    blurb: "Spa-inspired baths, finished to the millimeter.",
    desc: "Spa-inspired baths — tile, glass, and fixtures detailed and finished to the millimeter.",
    icon: Bath,
    imageSlot: "serviceBathroom",
  },
  {
    slug: "outdoor-living",
    num: "03",
    title: "Outdoor Living Spaces",
    blurb: "Decks, porches and poolside rooms for Lowcountry living.",
    desc: "Decks, screened porches, and poolside rooms that pull everyday life out into the Lowcountry air.",
    icon: TreePine,
    imageSlot: "serviceOutdoor",
  },
  {
    slug: "whole-home-renovations",
    num: "04",
    title: "Whole-Home Renovations",
    blurb: "Gut-to-finish transformations, one accountable team.",
    desc: "Gut-to-finish transformations, planned and managed start to finish by a single accountable team.",
    icon: House,
    imageSlot: "serviceWholeHome",
  },
  {
    slug: "design-to-build",
    num: "05",
    title: "Design to Build",
    blurb: "One partner from first sketch to final walkthrough.",
    desc: "One partner from first sketch through final walkthrough — design, permitting and construction under one roof.",
    icon: PencilRuler,
    imageSlot: "serviceDesignBuild",
  },
];

export interface WhyPoint {
  title: string;
  desc: string;
}

export const WHY_US: WhyPoint[] = [
  {
    title: "Local, Licensed & Insured",
    desc: "A Mount Pleasant firm you can verify — and trust in your home.",
  },
  {
    title: "On Time & On Budget",
    desc: "Clear schedules and honest pricing, with no surprises on the invoice.",
  },
  {
    title: "Craftsmanship & Detail",
    desc: "Finish work held to a standard you can see and feel for decades.",
  },
  {
    title: "Clear Communication",
    desc: "You know exactly what is happening at every step of the build.",
  },
];

// Homepage headline stats and client reviews are now CMS-configurable — see
// StatItem / DEFAULT_STATS and ReviewItem / DEFAULT_REVIEWS in lib/siteConfig.ts
// and the admin Photos tab.

/** Towns in the service area (shown as chips on the Contact / service-area sections). */
export const SERVICE_AREAS: string[] = [
  "Mount Pleasant",
  "Charleston",
  "Daniel Island",
  "Isle of Palms",
  "Sullivan's Island",
  "Awendaw",
  "Wando",
  "Hanahan",
  "Goose Creek",
];

/** Short trust tokens shown in the hero trust strip and footer. */
export const TRUST_TOKENS: string[] = [
  "Licensed",
  "Bonded",
  "Insured",
  "Free Estimates",
  "Lowcountry Based",
];

export interface NavItem {
  label: string;
  href: string;
}

export const NAV: NavItem[] = [
  { label: "Home", href: "/" },
  { label: "Services", href: "/services/" },
  { label: "About", href: "/about/" },
  { label: "Gallery", href: "/gallery/" },
  { label: "Contact", href: "/contact/" },
];

/** Project Type options for the estimate form's dropdown. */
export const PROJECT_TYPES: string[] = [
  "Kitchen Remodel",
  "Bathroom Renovation",
  "Outdoor Living Space",
  "Whole-Home Renovation",
  "Design to Build",
  "General Home Repairs",
];
